/**
 * Navigation inside an app, and the overlays an app may open. A control that holds a place (the open tab, the page) has a `name`
 * like a field does: the app reads it as `{{values.name}}`. Overlays are behaviour, not free-standing: a `sheet` or `modal` is a
 * button that opens components, and an action's `confirm` sentence is an alert dialog before it runs (the renderer, not a node).
 * OURS: the workbench defines this file; AIOE mirrors it.
 */
import { UI_LIMITS } from "../uiBase.js";
import { control } from "./kit.js";
const menuItem = (k) => k.z.object({ label: k.label.min(1), action: k.action, danger: k.z.boolean().optional(), separated: k.z.boolean().optional() }).strict();
export const NAVIGATION_CONTROLS = [
    control({
        type: "tabs",
        group: "navigation",
        heroui: "@heroui/react Tabs",
        summary: "tabs { name?, panels[{ id, label, children[] }], default?: panel id } — one panel at a time; with a name the open panel's id is that value",
        example: { type: "tabs", name: "tab", default: "one", panels: [{ id: "one", label: "Overview", children: [{ type: "text", text: "The summary." }] }, { id: "two", label: "Detail", children: [{ type: "text", text: "The detail." }] }] },
        field: "option",
        named: true,
        shape: (k) => ({ name: k.name.optional(), panels: k.panels(), default: k.z.string().max(32).optional() }),
    }),
    control({
        type: "accordion",
        group: "navigation",
        heroui: "@heroui/react Accordion",
        summary: "accordion { panels[{ id, label, children[] }], multiple?, open?: panel id } — headings that open and close their panel",
        example: { type: "accordion", multiple: true, panels: [{ id: "a", label: "What it reads", children: [{ type: "text", text: "Tasks and pull requests." }] }, { id: "b", label: "What it can do", children: [{ type: "text", text: "Move a task." }] }] },
        shape: (k) => ({ panels: k.panels(), multiple: k.z.boolean().optional(), open: k.z.string().max(32).optional() }),
    }),
    control({
        type: "disclosure",
        group: "navigation",
        heroui: "@heroui/react Disclosure",
        summary: "disclosure { label, open?, children[] } — one heading that shows or hides components",
        example: { type: "disclosure", label: "More detail", children: [{ type: "text", text: "Hidden until opened." }] },
        shape: (k) => ({ label: k.label.min(1), open: k.z.boolean().optional(), children: k.nodes() }),
    }),
    control({
        type: "disclosure-group",
        group: "navigation",
        heroui: "@heroui/react DisclosureGroup",
        summary: "disclosure-group { panels[{ id, label, children[] }], multiple? } — several disclosures; opening one closes the others unless multiple",
        example: { type: "disclosure-group", panels: [{ id: "a", label: "First", children: [{ type: "text", text: "One." }] }, { id: "b", label: "Second", children: [{ type: "text", text: "Two." }] }] },
        shape: (k) => ({ panels: k.panels(), multiple: k.z.boolean().optional() }),
    }),
    control({
        type: "breadcrumbs",
        group: "navigation",
        heroui: "@heroui/react Breadcrumbs",
        summary: "breadcrumbs { items[{ label, action? }] } — where the person is; an item with an action is pressable, the last is the current place",
        example: { type: "breadcrumbs", items: [{ label: "Projects", action: { do: "open-view", view: "projects" } }, { label: "Release" }] },
        shape: (k) => ({ items: k.z.array(k.z.object({ label: k.label.min(1), action: k.action.optional() }).strict()).min(1).max(8) }),
    }),
    control({
        type: "pagination",
        group: "navigation",
        heroui: "@heroui/react Pagination",
        summary: "pagination { name, total: number of pages | items + pageSize, pageSize?, default?: 1, onChange? } — page buttons; the current page is the value of name",
        example: { type: "pagination", name: "page", total: 12, default: 1 },
        field: "number",
        shape: (k) => ({ name: k.name, total: k.z.number().int().min(1).max(100_000), pageSize: k.z.number().int().min(1).max(1000).optional(), default: k.z.number().int().min(1).optional(), onChange: k.action.optional() }),
    }),
    control({
        type: "stepper",
        group: "navigation",
        heroui: "@heroui-pro/react Stepper",
        summary: "stepper { steps[{ label, hint? }], current?: 1-based step, orientation?: horizontal|vertical } — numbered stages with the current one marked",
        example: { type: "stepper", current: 2, steps: [{ label: "Pick", hint: "A repository" }, { label: "Describe" }, { label: "Review" }] },
        shape: (k) => ({ steps: k.z.array(k.z.object({ label: k.label.min(1), hint: k.hint.optional() }).strict()).min(2).max(UI_LIMITS.steps), current: k.z.union([k.z.number().int().min(1).max(UI_LIMITS.steps), k.z.string().max(40)]).optional(), orientation: k.z.enum(["horizontal", "vertical"]).optional() }),
    }),
    control({
        type: "toolbar",
        group: "navigation",
        heroui: "@heroui/react Toolbar",
        summary: "toolbar { label, children[] (buttons, button-groups, toggle-buttons, segments), orientation? } — a row of controls moved between with the arrow keys",
        example: { type: "toolbar", label: "Board tools", children: [{ type: "button", label: "Refresh", action: { do: "run", name: "refresh" } }, { type: "toggle-button", name: "compact", label: "Compact" }] },
        shape: (k) => ({ label: k.label.min(1), children: k.nodes(12), orientation: k.z.enum(["horizontal", "vertical"]).optional() }),
    }),
    control({
        type: "button-group",
        group: "navigation",
        heroui: "@heroui/react ButtonGroup",
        summary: "button-group { buttons[{ label, action, variant?: primary|secondary|danger }] } — buttons joined as one",
        example: { type: "button-group", buttons: [{ label: "Day", action: { do: "run", name: "day" } }, { label: "Week", action: { do: "run", name: "week" } }] },
        shape: (k) => ({ buttons: k.z.array(k.button).min(1).max(UI_LIMITS.buttons) }),
    }),
    control({
        type: "toggle-button",
        group: "navigation",
        heroui: "@heroui/react ToggleButton",
        summary: "toggle-button { name, label, default?, onChange? } — a button that stays pressed (the value is true or false)",
        example: { type: "toggle-button", name: "pinned", label: "Pinned", default: false },
        field: "boolean",
        shape: (k) => ({ name: k.name, label: k.label.min(1), default: k.z.boolean().optional(), onChange: k.action.optional() }),
    }),
    control({
        type: "toggle-button-group",
        group: "navigation",
        heroui: "@heroui/react ToggleButtonGroup",
        summary: "toggle-button-group { name, options[{ value, label }], multiple?, default?: string | string[], onChange? } — joined toggles; one at a time, or several with multiple",
        example: { type: "toggle-button-group", name: "layout", options: [{ value: "list", label: "List" }, { value: "grid", label: "Grid" }], default: "list" },
        field: "option",
        shape: (k) => ({ name: k.name, options: k.options(8), multiple: k.z.boolean().optional(), default: k.z.union([k.z.string().max(60), k.z.array(k.z.string().max(60)).max(8)]).optional(), onChange: k.action.optional() }),
    }),
    control({
        type: "dropdown",
        group: "navigation",
        heroui: "@heroui/react Dropdown",
        summary: "dropdown { label, items[{ label, action, danger?, separated? }], variant?: primary|secondary|ghost } — a button that opens a menu of declared actions",
        example: { type: "dropdown", label: "Actions", items: [{ label: "Archive", action: { do: "run", name: "archive" } }, { label: "Delete", danger: true, separated: true, action: { do: "run", name: "delete", confirm: "Delete this for good?" } }] },
        shape: (k) => ({ label: k.label.min(1), items: k.z.array(menuItem(k)).min(1).max(12), variant: k.z.enum(["primary", "secondary", "ghost"]).optional() }),
    }),
    control({
        type: "action-bar",
        group: "navigation",
        heroui: "@heroui-pro/react ActionBar",
        summary: "action-bar { label, buttons[{ label, action, variant? }] } — a bar of actions for what is selected (\"3 selected\" and what to do with them)",
        example: { type: "action-bar", label: "3 selected", buttons: [{ label: "Archive", action: { do: "run", name: "archive" } }, { label: "Assign", action: { do: "run", name: "assign" } }] },
        shape: (k) => ({ label: k.label.min(1), buttons: k.z.array(k.button).min(1).max(UI_LIMITS.buttons) }),
    }),
    control({
        type: "sheet",
        group: "overlays",
        heroui: "@heroui-pro/react Sheet",
        summary: "sheet { trigger, title?, description?, side?: right|bottom|left, children[] } — a button that slides a panel of components in (a \"details\" pattern)",
        example: { type: "sheet", trigger: "Details", title: "Task detail", description: "Everything about it", side: "right", children: [{ type: "text", text: "Owner Ana, due Friday." }] },
        shape: (k) => ({ trigger: k.label.min(1), title: k.label.optional(), description: k.hint.optional(), side: k.z.enum(["right", "bottom", "left"]).optional(), children: k.nodes() }),
    }),
    control({
        type: "modal",
        group: "overlays",
        heroui: "@heroui/react Modal",
        summary: "modal { trigger, title?, description?, size?: sm|md|lg, children[] } — a button that opens a dialog of components",
        example: { type: "modal", trigger: "Open", title: "Release notes", size: "md", children: [{ type: "markdown", text: "- Apps page\n- Controls" }] },
        shape: (k) => ({ trigger: k.label.min(1), title: k.label.optional(), description: k.hint.optional(), size: k.z.enum(["sm", "md", "lg"]).optional(), children: k.nodes() }),
    }),
];
//# sourceMappingURL=navigation.js.map